/**
 * ChatInput Component
 *
 * Chat input component with comprehensive features:
 * - Multi-line text input with auto-resize
 * - @mention trigger with autocomplete integration
 * - Send button with loading state
 * - Keyboard shortcuts (Enter to send, Shift+Enter for newline)
 * - Reply-to message preview
 * - Character limit indicator
 * - Disabled state when not connected
 *
 * @module components/chat/ChatInput
 */

import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  memo,
  forwardRef
} from 'react';
import {
  Send,
  X,
  CornerUpLeft,
  AtSign,
  Loader2,
  AlertCircle,
  Paperclip,
  Image,
  FileText
} from 'lucide-react';
import { useChat } from '../../contexts/ChatContext.jsx';
import { useMentions, extractMentionsFromContent } from '../../hooks/useMentions.js';

// ============================================
// Constants
// ============================================

/**
 * Maximum message length
 */
const MAX_MESSAGE_LENGTH = 4000;

/**
 * Minimum textarea rows
 */
const MIN_ROWS = 2;

/**
 * Maximum textarea rows before scrolling
 */
const MAX_ROWS = 8;

/**
 * Line height in pixels for row calculation
 */
const LINE_HEIGHT_PX = 24;

/**
 * Padding adjustment for textarea height calculation (py-3 = 24px total)
 */
const PADDING_PX = 24;

/**
 * Minimum height to match button stack (2x h-9 buttons + gap)
 */
const MIN_HEIGHT_PX = 80;

/**
 * Character count warning threshold (show warning when approaching limit)
 */
const CHAR_WARNING_THRESHOLD = 0.9;

/**
 * Keyboard shortcuts
 */
const KEYBOARD_SHORTCUTS = {
  SEND: 'Enter',
  NEWLINE: 'Shift+Enter',
  CANCEL_REPLY: 'Escape'
};

// File upload constants
const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_EXTENSIONS = [
  '.png', '.jpg', '.jpeg', '.gif', '.webp',
  '.txt', '.md', '.json', '.js', '.ts', '.jsx', '.tsx', '.py', '.go'
];

// ============================================
// Helper Functions
// ============================================

/**
 * Calculate the number of rows needed for the textarea
 * @param {string} text - Input text
 * @param {number} textareaWidth - Width of textarea in pixels
 * @returns {number} Number of rows
 */
function calculateRows(text, textareaWidth) {
  if (!text) return MIN_ROWS;

  // Count explicit newlines
  const lines = text.split('\n');

  // Estimate wrapped lines (rough calculation)
  const charsPerLine = Math.floor(textareaWidth / 8) || 50; // ~8px per char average
  let totalRows = 0;

  lines.forEach((line) => {
    const wrappedLines = Math.max(1, Math.ceil(line.length / charsPerLine));
    totalRows += wrappedLines;
  });

  return Math.min(Math.max(totalRows, MIN_ROWS), MAX_ROWS);
}

/**
 * Get character count status
 * @param {number} count - Current character count
 * @param {number} max - Maximum characters
 * @returns {object} { color, message, isWarning, isOver }
 */
function getCharCountStatus(count, max) {
  const ratio = count / max;

  if (count > max) {
    return {
      color: 'text-red-500',
      message: `${count - max} characters over limit`,
      isWarning: true,
      isOver: true
    };
  }

  if (ratio >= CHAR_WARNING_THRESHOLD) {
    return {
      color: 'text-yellow-500',
      message: `${max - count} characters remaining`,
      isWarning: true,
      isOver: false
    };
  }

  return {
    color: 'text-gray-500',
    message: `${count}/${max}`,
    isWarning: false,
    isOver: false
  };
}

/**
 * Validate a file for upload
 */
function validateFile(file) {
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  if (!ALLOWED_EXTENSIONS.includes(ext) && !file.type.startsWith('image/')) {
    return `File type not allowed: ${ext}`;
  }
  if (file.size > MAX_FILE_SIZE) {
    return `File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB (max 10MB)`;
  }
  return null;
}

// ============================================
// Sub-Components
// ============================================

/**
 * Reply preview banner
 * @param {object} props - Component props
 * @param {object} props.replyTo - Message being replied to
 * @param {Function} props.onCancel - Cancel reply callback
 * @returns {React.ReactElement} Reply preview
 */
const ReplyPreview = memo(function ReplyPreview({ replyTo, onCancel }) {
  if (!replyTo) return null;

  const truncatedContent = useMemo(() => {
    if (!replyTo.content) return 'Message';
    return replyTo.content.length > 100
      ? `${replyTo.content.slice(0, 100)}...`
      : replyTo.content;
  }, [replyTo.content]);

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-gray-800 border-b border-gray-700">
      <CornerUpLeft className="w-4 h-4 text-indigo-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-gray-400">
          Replying to{' '}
          <span className="font-medium text-indigo-400">
            {replyTo.senderName || 'Unknown'}
          </span>
        </div>
        <div className="text-sm text-gray-300 truncate">{truncatedContent}</div>
      </div>
      <button
        type="button"
        onClick={onCancel}
        className="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
        title="Cancel reply (Esc)"
        aria-label="Cancel reply"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
});

/**
 * Character count indicator
 * @param {object} props - Component props
 * @param {number} props.count - Current character count
 * @param {number} props.max - Maximum characters
 * @returns {React.ReactElement|null} Character count indicator
 */
const CharacterCount = memo(function CharacterCount({ count, max }) {
  const status = useMemo(() => getCharCountStatus(count, max), [count, max]);

  // Only show when approaching limit or over
  if (!status.isWarning && count < max * 0.75) return null;

  return (
    <span className={`text-xs ${status.color} flex items-center gap-1`}>
      {status.isOver && <AlertCircle className="w-3 h-3" />}
      {status.message}
    </span>
  );
});

/**
 * Send button component
 * @param {object} props - Component props
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {boolean} props.loading - Whether sending is in progress
 * @param {Function} props.onClick - Click handler
 * @returns {React.ReactElement} Send button
 */
const SendButton = memo(function SendButton({ disabled, loading, onClick }) {
  return (
    <button
      type="submit"
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        flex items-center justify-center w-9 h-9 rounded-xl
        transition-all duration-200
        ${
          disabled || loading
            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
            : 'bg-indigo-600 text-white hover:bg-indigo-500 active:bg-indigo-700 shadow-lg hover:shadow-indigo-500/25'
        }
      `}
      title={disabled ? 'Cannot send' : 'Send message (Enter)'}
      aria-label="Send message"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Send className="w-4 h-4" />
      )}
    </button>
  );
});

/**
 * Attach button - styled like SessionChatInput but sized like SendButton
 */
const AttachButton = memo(function AttachButton({ onClick, disabled, fileCount }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        flex items-center justify-center w-9 h-9 rounded-xl transition-all relative
        ${disabled
          ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
          : 'bg-gray-800 text-gray-400 hover:text-orange-400 hover:bg-gray-700'
        }
      `}
      title="Attach file"
    >
      <Paperclip className="w-4 h-4" />
      {fileCount > 0 && (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-indigo-600 text-white text-xs rounded-full flex items-center justify-center">
          {fileCount}
        </span>
      )}
    </button>
  );
});

/**
 * File preview list
 */
const FilePreview = memo(function FilePreview({ files, onRemove }) {
  if (!files || files.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 px-3 py-2 border-b border-gray-700">
      {files.map((f) => (
        <div
          key={f.id}
          className="relative group flex items-center gap-2 px-2 py-1 bg-gray-800 rounded-lg border border-gray-700"
        >
          {f.isImage && f.preview ? (
            <img src={f.preview} alt={f.name} className="w-8 h-8 object-cover rounded" />
          ) : (
            <FileText className="w-4 h-4 text-gray-400" />
          )}
          <span className="text-sm text-gray-300 max-w-[120px] truncate">{f.name}</span>
          <button
            type="button"
            onClick={() => onRemove(f.id)}
            className="p-0.5 rounded hover:bg-gray-600 text-gray-500 hover:text-gray-300"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
});

/**
 * Mention trigger button - sized to match AttachButton
 * @param {object} props - Component props
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.disabled - Whether button is disabled
 * @returns {React.ReactElement} Mention trigger button
 */
const MentionTriggerButton = memo(function MentionTriggerButton({
  onClick,
  disabled
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        flex items-center justify-center w-9 h-9 rounded-xl transition-all
        ${
          disabled
            ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
            : 'bg-gray-800 text-gray-400 hover:text-indigo-400 hover:bg-gray-700'
        }
      `}
      title="Insert @mention"
      aria-label="Insert mention"
    >
      <AtSign className="w-4 h-4" />
    </button>
  );
});

// ============================================
// Main Component
// ============================================

/**
 * ChatInput Component
 *
 * Input field for sending chat messages with @mention support.
 *
 * @param {object} props - Component props
 * @param {string} [props.placeholder='Type a message...'] - Input placeholder text
 * @param {object} [props.replyTo] - Message to reply to
 * @param {Function} [props.onCancelReply] - Callback when reply is cancelled
 * @param {Function} [props.onSend] - Callback when message is sent
 * @param {boolean} [props.disabled=false] - Whether input is disabled
 * @param {boolean} [props.autoFocus=false] - Whether to auto-focus on mount
 * @param {number} [props.maxLength=4000] - Maximum message length
 * @param {Array} [props.users=[]] - Available users for mentions
 * @param {Array} [props.agents=[]] - Available agents for mentions
 * @param {Function} [props.onMentionSearch] - Callback for remote mention search
 * @param {string} [props.className] - Additional CSS classes
 * @returns {React.ReactElement} Chat input component
 */
const ChatInput = forwardRef(function ChatInput(
  {
    placeholder = 'Type a message...',
    replyTo = null,
    onCancelReply,
    onSend,
    disabled = false,
    autoFocus = false,
    maxLength = MAX_MESSAGE_LENGTH,
    users = [],
    agents = [],
    onMentionSearch,
    className = ''
  },
  ref
) {
  // ----------------------------------------
  // State
  // ----------------------------------------

  const [inputValue, setInputValue] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  // File attachment state
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);

  // ----------------------------------------
  // Refs
  // ----------------------------------------

  const textareaRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Expose textarea ref via forwardRef
  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        ref(textareaRef.current);
      } else {
        ref.current = textareaRef.current;
      }
    }
  }, [ref]);

  // ----------------------------------------
  // Context
  // ----------------------------------------

  const {
    isConnected,
    activeChannel,
    activeDmRecipient,
    sendMessage,
    sendDm
  } = useChat();

  // ----------------------------------------
  // Mentions Hook
  // ----------------------------------------

  // Stable callback for mention selection - prevents infinite loop
  const handleMentionSelected = useCallback((mention, result) => {
    if (result) {
      setInputValue(result.text);
      setCursorPosition(result.cursorPosition);
      // Focus and set cursor position after state update
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(
            result.cursorPosition,
            result.cursorPosition
          );
        }
      }, 0);
    }
  }, []);

  const {
    isOpen: mentionsOpen,
    suggestions,
    selectedIndex,
    isLoading: mentionsLoading,
    handleKeyDown: handleMentionKeyDown,
    selectMention,
    getMentions,
    resolveMentionIds,
    close: closeMentions
  } = useMentions({
    inputValue,
    cursorPosition,
    users,
    agents,
    onSearchUsers: onMentionSearch,
    onSearchAgents: onMentionSearch,
    onMentionSelected: handleMentionSelected
  });

  // ----------------------------------------
  // File Handlers
  // ----------------------------------------

  const addFiles = useCallback((files) => {
    setUploadError(null);
    const fileArray = Array.from(files);

    if (attachedFiles.length + fileArray.length > MAX_FILES) {
      setUploadError(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    const newFiles = [];
    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        setUploadError(error);
        return;
      }

      const isImage = file.type.startsWith('image/');
      newFiles.push({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        isImage,
        preview: isImage ? URL.createObjectURL(file) : null,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      });
    }

    setAttachedFiles((prev) => [...prev, ...newFiles]);
  }, [attachedFiles.length]);

  const removeFile = useCallback((id) => {
    setAttachedFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const files = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }

    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  }, [addFiles]);

  const handleFileSelect = useCallback((e) => {
    if (e.target.files?.length > 0) {
      addFiles(e.target.files);
    }
    e.target.value = '';
  }, [addFiles]);

  // ----------------------------------------
  // Computed Values
  // ----------------------------------------

  /**
   * Whether the input can be submitted
   */
  const canSubmit = useMemo(() => {
    const trimmed = inputValue.trim();
    const hasContent = trimmed.length > 0 || attachedFiles.length > 0;
    return (
      hasContent &&
      trimmed.length <= maxLength &&
      isConnected &&
      !disabled &&
      !isSending &&
      (activeChannel || activeDmRecipient)
    );
  }, [
    inputValue,
    attachedFiles.length,
    maxLength,
    isConnected,
    disabled,
    isSending,
    activeChannel,
    activeDmRecipient
  ]);

  /**
   * Placeholder text based on state
   */
  const dynamicPlaceholder = useMemo(() => {
    if (!isConnected) return 'Connecting...';
    if (disabled) return 'Input disabled';
    if (!activeChannel && !activeDmRecipient) return 'Select a channel to chat';
    return placeholder;
  }, [isConnected, disabled, activeChannel, activeDmRecipient, placeholder]);

  /**
   * Number of textarea rows
   */
  const rows = useMemo(() => {
    const width = textareaRef.current?.offsetWidth || 300;
    return calculateRows(inputValue, width);
  }, [inputValue]);

  // ----------------------------------------
  // Handlers
  // ----------------------------------------

  /**
   * Handle input change
   */
  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setCursorPosition(e.target.selectionStart);
    setSendError(null);
  }, []);

  /**
   * Handle input selection change (for cursor tracking)
   */
  const handleSelect = useCallback((e) => {
    setCursorPosition(e.target.selectionStart);
  }, []);

  /**
   * Handle message send
   */
  const handleSend = useCallback(async () => {
    if (!canSubmit) return;

    const content = inputValue.trim();
    if (!content && attachedFiles.length === 0) return;

    setIsSending(true);
    setSendError(null);

    try {
      // Extract mentions from content
      const mentions = getMentions(content);
      const { userIds, agentIds } = resolveMentionIds(mentions);

      const sendOptions = {
        replyToId: replyTo?.id || null,
        mentionedUserIds: userIds,
        mentionedAgentIds: agentIds,
        attachments: attachedFiles
      };

      if (activeChannel) {
        await sendMessage(activeChannel.id, content, sendOptions);
      } else if (activeDmRecipient) {
        await sendDm(activeDmRecipient.id, content, {
          replyToId: replyTo?.id || null,
          attachments: attachedFiles
        });
      }

      // Clear input and attachments on success
      setInputValue('');
      setCursorPosition(0);
      setAttachedFiles([]);

      // Clear reply state
      if (replyTo && onCancelReply) {
        onCancelReply();
      }

      // Notify parent
      if (onSend) {
        onSend(content);
      }

      // Refocus textarea
      textareaRef.current?.focus();
    } catch (error) {
      setSendError(error.message || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  }, [
    canSubmit,
    inputValue,
    attachedFiles,
    getMentions,
    resolveMentionIds,
    replyTo,
    activeChannel,
    activeDmRecipient,
    sendMessage,
    sendDm,
    onCancelReply,
    onSend
  ]);

  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback(
    (e) => {
      // Let mention dropdown handle navigation keys first
      if (mentionsOpen && handleMentionKeyDown(e)) {
        return;
      }

      // Cancel reply on Escape
      if (e.key === 'Escape' && replyTo) {
        e.preventDefault();
        onCancelReply?.();
        return;
      }

      // Close mentions on Escape
      if (e.key === 'Escape' && mentionsOpen) {
        e.preventDefault();
        closeMentions();
        return;
      }

      // Send on Enter (without Shift)
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
        return;
      }

      // Shift+Enter inserts newline (default behavior)
    },
    [mentionsOpen, handleMentionKeyDown, replyTo, onCancelReply, closeMentions, handleSend]
  );

  /**
   * Handle mention button click
   */
  const handleMentionClick = useCallback(() => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = inputValue;

    // Insert @ at cursor position
    const needsSpace = start > 0 && text[start - 1] !== ' ' && text[start - 1] !== '\n';
    const insertText = needsSpace ? ' @' : '@';
    const newText = text.slice(0, start) + insertText + text.slice(end);
    const newCursor = start + insertText.length;

    setInputValue(newText);
    setCursorPosition(newCursor);

    // Focus and set cursor
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursor, newCursor);
    }, 0);
  }, [inputValue]);

  /**
   * Handle cancel reply
   */
  const handleCancelReply = useCallback(() => {
    onCancelReply?.();
    textareaRef.current?.focus();
  }, [onCancelReply]);

  // ----------------------------------------
  // Effects
  // ----------------------------------------

  // Auto-focus on mount
  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  // Focus when channel/DM changes
  useEffect(() => {
    if ((activeChannel || activeDmRecipient) && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [activeChannel, activeDmRecipient]);

  // ----------------------------------------
  // Render
  // ----------------------------------------

  return (
    <div
      ref={containerRef}
      className={`relative bg-gray-900 border-t border-gray-700 ${className}`}
    >
      {/* Reply preview */}
      {replyTo && (
        <ReplyPreview replyTo={replyTo} onCancel={handleCancelReply} />
      )}

      {/* File preview */}
      <FilePreview files={attachedFiles} onRemove={removeFile} />

      {/* Upload error */}
      {uploadError && (
        <div className="mx-3 mt-2 px-3 py-2 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm flex justify-between items-center">
          <span>{uploadError}</span>
          <button type="button" onClick={() => setUploadError(null)} className="text-red-500 hover:text-red-400">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ALLOWED_EXTENSIONS.join(',')}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Mention dropdown */}
      {mentionsOpen && suggestions.length > 0 && (
        <div className="absolute bottom-full left-0 right-0 mb-1 mx-3">
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden max-h-64 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.id}
                type="button"
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-left
                  ${
                    index === selectedIndex
                      ? 'bg-indigo-600/20 text-white'
                      : 'text-gray-300 hover:bg-gray-700/50'
                  }
                  transition-colors
                `}
                onClick={() => selectMention(suggestion)}
                onMouseEnter={() => {
                  // Could update selected index on hover
                }}
              >
                <div
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                    ${
                      suggestion.type === 'agent'
                        ? 'bg-indigo-600 text-indigo-100'
                        : 'bg-emerald-600 text-emerald-100'
                    }
                  `}
                >
                  {(suggestion.displayName || suggestion.name || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {suggestion.displayName || suggestion.name}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {suggestion.type === 'agent' ? 'Agent' : 'User'}
                    {suggestion.operationalStatus && (
                      <span className="ml-1 capitalize">
                        &middot; {suggestion.operationalStatus}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
            {mentionsLoading && (
              <div className="flex items-center justify-center py-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Searching...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="flex items-end gap-2 p-3">
        {/* Button stack: @ above attachment */}
        <div className="flex flex-col gap-1">
          {/* Mention trigger button */}
          <MentionTriggerButton
            onClick={handleMentionClick}
            disabled={disabled || !isConnected}
          />

          {/* Attach button */}
          <AttachButton
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || !isConnected}
            fileCount={attachedFiles.length}
          />
        </div>

        {/* Text input */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={handleInputChange}
            onSelect={handleSelect}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={dynamicPlaceholder}
            disabled={disabled || !isConnected || (!activeChannel && !activeDmRecipient)}
            rows={3}
            className={`
              w-full resize-none rounded-xl px-4 py-3
              bg-gray-800 border border-gray-700
              text-white placeholder-gray-500
              focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all duration-200
            `}
            style={{
              minHeight: `${MIN_HEIGHT_PX}px`,
              maxHeight: `${MAX_ROWS * LINE_HEIGHT_PX + PADDING_PX}px`
            }}
            aria-label="Message input"
            aria-describedby="char-count"
          />
        </div>

        {/* Send button */}
        <SendButton
          onClick={handleSend}
          disabled={!canSubmit}
          loading={isSending}
        />
      </div>

      {/* Footer with character count and error - compact */}
      <div className="flex items-center justify-end px-3 pb-1.5 gap-2">
        {/* Error message */}
        {sendError && (
          <span className="text-xs text-red-500 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {sendError}
          </span>
        )}

        {/* Character count */}
        <CharacterCount count={inputValue.length} max={maxLength} id="char-count" />
      </div>
    </div>
  );
});

// ============================================
// Exports
// ============================================

export default ChatInput;

export {
  ChatInput,
  ReplyPreview,
  CharacterCount,
  SendButton,
  MentionTriggerButton,
  calculateRows,
  getCharCountStatus,
  MAX_MESSAGE_LENGTH,
  MIN_ROWS,
  MAX_ROWS,
  LINE_HEIGHT_PX,
  PADDING_PX,
  MIN_HEIGHT_PX,
  CHAR_WARNING_THRESHOLD,
  KEYBOARD_SHORTCUTS
};
